import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DKLabel, DKButton, DKSpinner, DKInput, showToast, TOAST_TYPE, } from "deskera-ui-library";
import { FORM_FIELDS } from "./ExpenseLimitHelper"
import { selectExpenseCategories } from "../../../Redux/Slices/ExpenseCategorySlice";
import Utility, { deepClone } from "../../../utility/Utility";
import EmployeeListService from "../../../services/EmployeeListService";
import { TENANT, TenantService } from "../../../managers/TenantManagerService";
import ExpenseLimitPolicyService from "../../../services/ExpenseLimitPolicyService";
import { INPUT_TYPE } from "../../../constants/Constant";
import MessageInfo from "../../common/MessageInfo";
import { useTranslation } from "react-i18next";

const AddExpenseLimitPopup = (props) => {

  const { t } = useTranslation();

  /** Selectors */
  const expenseCategories = useSelector(selectExpenseCategories);

  /**Hooks **/
  const [isEdit, setIsEditFlag] = useState(props.isEdit);
  const [isProcessing, setProcessingFlag] = useState(true);
  const [formFields, setFormFields] = useState(FORM_FIELDS);
  const [formValues, setFormValues] = useState({});
  const [apiCallInProgress, setApiCallInProgress] = useState(false);
  const [bannerData, setBannerData] = useState({
    visible: true,
    label: t("EXPENSE_LIMIT_INFO_MSG")
  });
  const [categoryData, setCategoryData] = useState(expenseCategories);


  /** Dispatcher */
  const dispatch = useDispatch();

  useEffect(() => {
    if (!Utility.isEmpty(expenseCategories)) {
      let updatedCategories = expenseCategories;
      if(!isEdit) {
        updatedCategories = expenseCategories.filter(e => Utility.isEmpty(e.expenseCategoryLimit));
      } else if (!Utility.isEmpty(props?.formData?.rowData) && isEdit) {
        updatedCategories = updateCategories(expenseCategories, props.formData.rowData);
        setValuesInUpdate(props.formData.rowData,updatedCategories);
      }
      updateFieldOptions("category", updatedCategories, expenseCategories);
    } else if (expenseCategories) {
      setProcessingFlag(false);
      if(!isEdit) {
        setBannerData({
          visible: true,
          label: t("EXPENSE_LIMIT_NO_CATEGORIES")
        });
      }
    }
  }, [expenseCategories, props.formData]);

  const updateCategories = (expenseCategories, formData) => {
    let updatedCategories = [];
    if(!Utility.isEmpty(formData) && Array.isArray(expenseCategories)) {
      updatedCategories = expenseCategories.filter(e => Utility.isEmpty(e.expenseCategoryLimit));
      formData?.expenseCategoryLimits.forEach(f => {
        if(!Utility.isEmpty(f.expenseCategoryId)) {
          updatedCategories.push(f.expenseCategoryId);
        }
      })
    }

    return updatedCategories
  }

  const calculateFieldMinLimit = (f, categoryData) => {
    let minLimit = 0;
    let categoryLimitData = [];
    if (Array.isArray(categoryData)) {
      categoryData.forEach((c) => {
        switch (f.columnCode) {
          case "categoryLimit":
            const utilisedCategoryLimit = parseFloat(c["expenseCategoryLimit"])
              - parseFloat(c['availableCategoryLimit']);
            if (utilisedCategoryLimit > minLimit) {
              minLimit = utilisedCategoryLimit;
              if (c?.expenseCategoryId?.name) {
                categoryLimitData.push({ name: c.expenseCategoryId.name, utilisedLimit: utilisedCategoryLimit, field: "Category Limit", fieldId: f.columnCode });
              }
            }
            break;
          case "totalMonthlyLimit":
            const utilisedMonthlyLimit = parseFloat(c["monthlyLimit"])
              - parseFloat(c['availableMonthlyLimit']);
            if (utilisedMonthlyLimit > minLimit) {
              minLimit = utilisedMonthlyLimit;
              if (c?.expenseCategoryId?.name) {
                categoryLimitData.push({ name: c.expenseCategoryId.name, utilisedLimit: utilisedMonthlyLimit, field: "Monthly Limit", fieldId: f.columnCode });
              }
            }
            break;
          case "totalYearlyLimit":
            const utilisedYearlyLimit = parseFloat(c["yearlyLimit"])
              - parseFloat(c['availableYearlyLimit']);
            if (utilisedYearlyLimit > minLimit) {
              minLimit = utilisedYearlyLimit;
              if (c?.expenseCategoryId?.name) {
                categoryLimitData.push({ name: c.expenseCategoryId.name, utilisedLimit: utilisedYearlyLimit, field: "Yearly Limit", fieldId: f.columnCode });
              }
            }
            break;
        }
      });
    }
    return { minLimit, categoryLimitData };
  }

  const updateFormValidation = (expenseLimitsData) => {
    const alteredConfig = [];
    formFields.forEach(f => {
      if (f.type === INPUT_TYPE.NUMBER) {
        const categoryLimit =  expenseLimitsData?.expenseCategoryLimits.filter((f) => !f.deleted);
        const { minLimit, categoryLimitData } = calculateFieldMinLimit(f, categoryLimit);
        if (Array.isArray(categoryLimitData) && categoryLimitData.length > 0) {
          let group = {
            validator: "minValue",
            value: minLimit
          }
          f.validation.push(group);
          alteredConfig.push({
            columnCode: f.columnCode,
            validation: f.validation,
            tooltipMsg: `<div class="row flex-wrap">${categoryLimitData.map((f, i) => `<div>${f.name} has already utilised ${f.utilisedLimit} from ${f.field}</div>`).join('')}</div>`
          })
        }
      }
    });
    EmployeeListService.getAlteredConfigDetails(formFields, alteredConfig, false);
  }

  const setValuesInUpdate = (values, updatedCategories) => {
    const categories = getCategoryDataInEdit(values?.expenseCategoryLimits);
    let formData = {
      id: values?.id,
      category: EmployeeListService.getValueFromMuliSelect(categories, updatedCategories, "id"),
      categoryLimit: values?.expenseCategoryLimit,
      oneTimeExpenseLimit: values?.oneTimeExpenseLimit,
      totalMonthlyLimit: values?.monthlyLimit,
      totalYearlyLimit: values?.yearlyLimit,
    }
    updateFormValidation(values);
    setFormValues(formData);
  }

  const getCategoryDataInEdit = (limitsData) => {
    const categoryIds = [];
    if (Array.isArray(limitsData)) {
      limitsData.forEach((l) => {
        if (!Utility.isEmpty(l.expenseCategoryId)) {
          categoryIds.push(l.expenseCategoryId?.id);
        }

      })
    }
    return categoryIds;
  }

  const updateFieldOptions = (fieldId, options, categories) => {
    const formFieldsCopy = deepClone(formFields);
    const alteredConfig = []
    if (Array.isArray(options)) {
      options = options.map(o => {
        return {
          id: o.id,
          name: o.name,
          ...o
        }
      });

      alteredConfig.push({ columnCode: fieldId, options: options });
      EmployeeListService.getAlteredConfigDetails(formFieldsCopy,
        alteredConfig, false, {}, {});
      if (!isEdit) {
        setBannerData({
          visible: true,
          label: categories.length === options.length ?
            t("EXPENSE_LIMIT_INFO_MSG") : options.length === 0 ? t("EXPENSE_LIMI_ALL_CATEGORIES") :
              t("EXPENSE_LIMIT_INFO_MSG")
        });
      }
      setProcessingFlag(false);
      setFormFields(formFieldsCopy);
      setCategoryData(options);
    }
  }

  const checkValidation = (selectedField) => {
    let isValid = true;
    let errMsg = "";
    const validation = selectedField?.validation;
    if (Array.isArray(validation)) {
      for (let i = 0; i < validation.length; i++) {
        const value = formValues[selectedField.columnCode];
        const validationFields = validation[i].field;
        if (Array.isArray(validationFields)) {
          for (let j = 0; j < validationFields.length; j++) {
            const field = validationFields[j];
            const fieldValue = formValues[field.id];
            switch (validation[i].validator) {
              case "lessThan":
                if ((typeof fieldValue === "string" || typeof fieldValue === "number")
                  && value) {
                  if (parseFloat(value) <= parseFloat(fieldValue)) {
                    isValid = true;
                    errMsg = ""
                  } else {
                    isValid = false;
                    errMsg = `Entered value should be less than <b>${field.name}</b>`;
                    break;
                  }
                }
                break;
              case "greaterThan":
                if ((typeof fieldValue === "string" || typeof fieldValue === "number")
                  && value) {
                  if (parseFloat(value) >= parseFloat(fieldValue)) {
                    isValid = true;
                    errMsg = ""
                  } else {
                    isValid = false;
                    errMsg = `Entered value should be greater than <b>${field.name}</b>`;
                  }
                }
                break;
              case "required":
                if (!Utility.isEmpty(fieldValue)) {
                  isValid = true;
                  errMsg = ""
                } else {
                  isValid = false;
                  errMsg = `Enter a valid <b>${field.name}</b>`;
                }
            }

            if (!isValid) {
              break;
            }
          }
        } else {
          switch (validation[i].validator) {
            case "minValue":
              if ((typeof value === "string" || typeof value === "number") && value) {
                if (parseFloat(value) >= parseFloat(validation[i].value)) {
                  isValid = true;
                  errMsg = ""
                } else {
                  isValid = false;
                  errMsg = `Entered value should be greater than <b>${validation[i].value}</b>`;
                }
              }
              break;
          }
        }
        if (!isValid) {
          break;
        }
      }
    }
    return { isValid, errMsg };
  }

  const updateColumnConfigDependentFields = (dependentFields, formfields, alteredConfig) => {
    if (Array.isArray(dependentFields)) {
      let valid = true;
      for (let d = 0; d < dependentFields.length; d++) {
        let field = formfields.find(fc => fc.columnCode === dependentFields[d]);
        if (!Utility.isEmpty(field)) {
          let { isValid, errMsg } = checkValidation(field);
          alteredConfig.push({ columnCode: dependentFields[d], hasError: !isValid, errMsg: errMsg });
          valid = isValid;
        }
        if (!valid) {
          break;
        }
      }
    }
  }

  const onChange = (v, i, f) => {
    const fieldConfigCopy = deepClone(formFields);
    if(typeof v === "string") {
      v = v.trim();
    }
    formValues[f.columnCode] = v;
    let { isValid, errMsg } = checkValidation(f);
    const alteredConfig = [{ columnCode: f.columnCode, hasError: !isValid, errMsg: errMsg }];
    if (isValid && f?.dependentFields) {
      updateColumnConfigDependentFields(f.dependentFields, fieldConfigCopy, alteredConfig);
    }
    EmployeeListService.getAlteredConfigDetails(fieldConfigCopy, alteredConfig, false);
    setFormFields(fieldConfigCopy);
  }

  const isInValid = () => {

    let invalid = false;
    const fieldConfigCopy = deepClone(formFields);
    const alteredConfig = [];
    for (let i = 0; i < fieldConfigCopy.length; i++) {
      const f = fieldConfigCopy[i];
      let { isValid, errMsg } = checkValidation(f);
      alteredConfig.push({ columnCode: f.columnCode, hasError: !isValid, errMsg: errMsg });
      if (isValid && f?.dependentFields) {
        updateColumnConfigDependentFields(f.dependentFields, fieldConfigCopy, alteredConfig);
      }
      invalid = alteredConfig.some(a => a.hasError);
    }
    EmployeeListService.getAlteredConfigDetails(fieldConfigCopy, alteredConfig, false);
    setFormFields(fieldConfigCopy);

    return invalid;
  }

  const preparePayloadData = () => {
    let payload = {
      expenseCategories: !Utility.isEmpty(formValues["category"]) ?
        EmployeeListService.getValueForMultiSelect(formValues["category"], categoryData, "id") : [],
      expenseCategoryLimit: !Utility.isEmpty(formValues["categoryLimit"]) ? parseFloat(formValues["categoryLimit"]) : 0,
      currency: TenantService.get(TENANT.CURRENCY_CODE),
      oneTimeExpenseLimit: !Utility.isEmpty(formValues["oneTimeExpenseLimit"]) ? parseFloat(formValues["oneTimeExpenseLimit"]) : 0,
      monthlyLimit: !Utility.isEmpty(formValues["totalMonthlyLimit"]) ? parseFloat(formValues["totalMonthlyLimit"]) : 0,
      yearlyLimit: !Utility.isEmpty(formValues["totalYearlyLimit"]) ? parseFloat(formValues["totalYearlyLimit"]) : 0
    }
    return payload;
  }

  const onSave = () => {
    if (isInValid()) {
      return;
    }
    setApiCallInProgress(true);
    const payload = preparePayloadData();
    if (!Utility.isEmpty(formValues['id'])) {
      payload['id'] = formValues.id;
      ExpenseLimitPolicyService.updateExpenseLimitPolicy(payload).then((res) => {
        if (res) {
          showToast(`Successfully updated limit policy`, TOAST_TYPE.SUCCESS);
          props.onClose(true);
        }
      }, err => {
        showToast(`Error updating limit policy`, TOAST_TYPE.FAILURE);
        props.onClose(false);
      })
    } else {
      ExpenseLimitPolicyService.saveExpenseLimitPolicy(payload).then((res) => {
        if (res) {
          showToast(`Successfully saved limit policy`, TOAST_TYPE.SUCCESS);
          props.onClose(true);
        }
      }, err => {
        showToast(`Error posting limit policy`, TOAST_TYPE.FAILURE);
        props.onClose(false);
      })
    }
    
  }

  const renderFormField = (f, i) => {
    return (
      <>
        <div id={f.columnCode} className="parent-width">
          <DKInput
            displayKey="name"
            className={"p-v-s mb-l"}
            icon={f.icon}
            title={f.name}
            placeholder={f.name}
            value={formValues[f.columnCode]}
            type={f.type}
            labelClassName=" text-align-left "
            onChange={(v) => {
              onChange(v, i, f);
            }}
            tooltip={{
              content: f.tooltipMsg,
              className: "warning-tooltip-prop",
            }}
            formatter={(obj) => {
              /** How to render a selected option in case of object **/
              return `${obj.name}`;
            }}
            dropdownConfig={{
              className: "",
              style: {},
              allowSearch: f.options?.length > 3,
              searchableKey: "name",
              checkMarkColor: "bg-app",
              data: f.options,
              multiSelect: f?.multiSelect,
              renderer: (index, obj) => {
                /** How to render value in Dropdown in case of object **/
                return <DKLabel text={`${obj.name}`} />;
              },
              onSelect: (index, obj) => {
                // onselect
              },
            }}
            direction={f.direction}
            required={f.required}
            readOnly={apiCallInProgress}
            canValidate={f.hasError}
            errorMessage={f.errMsg}
            validator={(value) => {
              return !f.hasError
            }}
          />
        </div>
      </>
    )
  }

  const getFormSection = () => {
    return (
      <>
        {formFields.map((f, i) => {
          if (f?.uivisible) {
            return renderFormField(f, i)
          }
        })}
      </>
    )
  }

  const getHeader = () => {
    return (
      <div className="row justify-content-between p-h-r p-v-s bg-gray1">
        <div className="row width-auto">
          <DKLabel
            text={`${!isEdit ? "Add Expense Limit" : "Update Expense Limit"}`}
            className="fw-b fs-m"
          />
          {isProcessing && <DKSpinner className="pl-s" />}
        </div>
        <div className="row width-auto">
          <DKButton
            title="Cancel"
            className="bg-white border-m mr-r"
            onClick={() => {
              props.onClose(false);
            }}
          />
          <div className="row border-radius-m border-m">
            <DKButton
              title={`${isEdit ? "Update":"Save"}`}
              className={`${apiCallInProgress
                ? 'border-radius-none text-gray'
                : 'bg-app text-white'
                }`}
              onClick={() => {
                onSave();
              }}
              disabled={apiCallInProgress}
            />
            {apiCallInProgress && (
              <DKSpinner iconClassName="ic-s" className="column pl-0 pr-s" />
            )}
          </div>

        </div>
      </div>
    );
  }

  return (
    <>
      <div className="transparent-background">
        <div className="popup-window"
          style={{
            maxWidth: 600,
            width: '90%',
            maxHeight: '95%',
            padding: 0,
            paddingBottom: 10,
            overflowY: 'hidden'
          }}>
          {getHeader()}
          {bannerData.visible && (
            <MessageInfo
                    message={[{ message: `${bannerData.label}` }]}
                    bannerClass="p-h-s p-v-s"
                    isClosable={false}
            />
          )}
          <div className="column p-h-xl parent-width parent-height hide-scroll-bar"
            style={{
              overflowY: 'scroll',
              overflowX: 'hidden',
              scrollbarWidth: 'none'
            }}>
            <div
              id="popup-container"
              className="column parent-width parent-height"
              style={{
                pointerEvents: apiCallInProgress ? 'none' : 'auto'
              }}
            >

              {getFormSection()}
            </div>
          </div>
        </div>
      </div>
    </>
  )

}

export default AddExpenseLimitPopup;